import { useEffect, useRef, useState } from 'react';

/** Cancel stale UI results on retry, navigation, or account changes. */
export function useAsyncRead<T>(load: () => Promise<T>, owner: string, initial: T) {
  const [attempt, setAttempt] = useState(0);
  const request = useRef<{
    owner: string;
    attempt: number;
    load: typeof load;
    promise: Promise<T>;
  } | null>(null);
  const [result, setResult] = useState<{
    owner: string;
    attempt: number;
    load: typeof load;
    data: T;
    error: boolean;
  } | null>(null);
  useEffect(() => {
    let active = true;
    if (
      !request.current ||
      request.current.owner !== owner ||
      request.current.attempt !== attempt ||
      request.current.load !== load
    ) {
      request.current = { owner, attempt, load, promise: load() };
    }
    void request.current.promise.then(
      (data) => {
        if (active) setResult({ owner, attempt, load, data, error: false });
      },
      () => {
        if (active) setResult({ owner, attempt, load, data: initial, error: true });
      },
    );
    return () => {
      active = false;
    };
  }, [load, owner, attempt, initial]);
  const current =
    result?.owner === owner && result.attempt === attempt && result.load === load ? result : null;
  return {
    data: current?.data ?? initial,
    loading: !current,
    error: current?.error ?? false,
    retry: () => setAttempt((n) => n + 1),
  };
}
